import { webApi } from '@mi-front/api-clients';
import { Box, Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { useFragment } from '../../hooks/useFragment';
import { getProjectRenderedVideoUrl } from './get-project-rendered-video-url';
import { ProjectCardDownloadButton } from './ProjectCardDownloadButton';
import { ProjectCardMediaArea } from './ProjectCardMediaArea';
import { ProjectCardOverflowMenu } from './ProjectCardOverflowMenu';
import { ProjectEditButton } from './ProjectEditButton';
import { ProjectPublishDropdownButton } from './ProjectPublishDropdownButton';
import { useProjectCardDelete } from './useProjectCardDelete';
import { useVideoEditorProjectUrl } from './useVideoEditorProjectUrl';

export interface ProjectCardProps {
  project: webApi.EnrichedVideoEditorProjectListItemDto;
  /** Social uploads by platform; used to list platforms that still allow a new publish from the card. */
  uploadsByPlatform: Map<string, webApi.FragmentSocialUploadItemDto[]> | undefined;
  onClone: () => void;
}

/**
 * Card for a single video editor project: name, status, project thumbnail preview, and actions
 * (download next to overflow when generated and URL is ready; otherwise download + edit on the left;
 * publish when generated; clone/delete in overflow menu).
 */
export function ProjectCard({ project, uploadsByPlatform, onClone }: ProjectCardProps) {
  const { fragment } = useFragment();
  const editorUrl = useVideoEditorProjectUrl(project.id);
  const { url: renderedUrl } = getProjectRenderedVideoUrl(project, { mode: 'download' });
  const hasRenderedVideo = !!renderedUrl;
  const hasRenderJob = !!project.renderJob;
  const isGeneratedProject = project.renderJob?.status === 'COMPLETED';
  const thumbnailSrc =
    typeof project.thumbnailUrl === 'string' && project.thumbnailUrl.length > 0
      ? project.thumbnailUrl
      : null;

  const { handleDelete } = useProjectCardDelete(project.id, fragment.id);

  return (
    <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <ProjectCardMediaArea
          thumbnailSrc={thumbnailSrc}
          renderJob={project.renderJob}
          publishedPlatforms={project.publishedPlatforms}
        />
        <CardContent sx={{ flexGrow: 1, pb: 0 }}>
          <Typography variant="subtitle1" component="h3">
            {project.name}
          </Typography>
        </CardContent>
        <CardActions
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            {!isGeneratedProject ? (
              <ProjectEditButton editorUrl={editorUrl} />
            ) : (
              <ProjectPublishDropdownButton
                projectId={project.id}
                uploadsByPlatform={uploadsByPlatform}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isGeneratedProject && renderedUrl ? (
              <ProjectCardDownloadButton href={renderedUrl} />
            ) : null}
            <ProjectCardOverflowMenu
              onClone={onClone}
              showDelete={!hasRenderedVideo && !hasRenderJob}
              onDelete={handleDelete}
            />
          </Box>
        </CardActions>
      </Card>
    </Grid>
  );
}
